import type { CliPromptTurnState, BridgePromptTurnServerState } from '@buildautomaton/types';
import { withCliSqlite } from '../sqlite/cli-database.js';

const MERGEABLE_BRIDGE_SERVER_STATES = new Set<string>([
  'queued',
  'requeued',
  'requeued_with_revert',
  'cancel_requested',
  'stopping',
  'discarded',
]);

export type PersistedPromptTurn = {
  turnId: string;
  sessionId: string;
  turnOrd: number;
  bridgeServerState: BridgePromptTurnServerState;
  lastCliState: CliPromptTurnState | null;
  payload: Record<string, unknown>;
};

export type PersistedQueueFile = {
  queueKey: string;
  updatedAt: string;
  turns: PersistedPromptTurn[];
};

export async function readPersistedQueue(queueKey: string): Promise<PersistedQueueFile | null> {
  return withCliSqlite((db) => {
    const row = db.get('SELECT queue_key, updated_at, turns_json FROM prompt_queue WHERE queue_key = ?', [
      queueKey,
    ]) as { queue_key: string; updated_at: string; turns_json: string } | null;
    if (!row) return null;
    try {
      const turns = JSON.parse(row.turns_json) as unknown;
      if (!Array.isArray(turns)) return null;
      return { queueKey: row.queue_key, updatedAt: row.updated_at, turns: turns as PersistedPromptTurn[] };
    } catch {
      return null;
    }
  });
}

export async function writePersistedQueue(file: PersistedQueueFile): Promise<void> {
  await withCliSqlite((db) => {
    db.run(
      `INSERT INTO prompt_queue (queue_key, updated_at, turns_json) VALUES (?, ?, ?)
       ON CONFLICT(queue_key) DO UPDATE SET updated_at = excluded.updated_at, turns_json = excluded.turns_json`,
      [file.queueKey, file.updatedAt, JSON.stringify(file.turns)],
    );
  });
}

export async function mergeBridgeServerQueueSnapshot(queueKey: string, serverTurns: unknown[]): Promise<PersistedQueueFile> {
  const prev = await readPersistedQueue(queueKey);
  const turns: PersistedPromptTurn[] = [];
  for (const raw of serverTurns) {
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as Record<string, unknown>;
    const turnId = typeof o.turnId === 'string' ? o.turnId : '';
    const sessionId = typeof o.sessionId === 'string' ? o.sessionId : '';
    const turnOrd = typeof o.turnOrd === 'number' ? o.turnOrd : Number(o.turnOrd) || 0;
    const bridgeServerState = (o.serverState ?? o.bridgeServerState) as BridgePromptTurnServerState;
    const lastCliState = (o.lastClientState ?? o.lastCliState ?? null) as CliPromptTurnState | null;
    const payload = o.payload && typeof o.payload === 'object' ? (o.payload as Record<string, unknown>) : {};
    if (!turnId || !sessionId) continue;
    if (!MERGEABLE_BRIDGE_SERVER_STATES.has(String(bridgeServerState))) continue;
    const old = prev?.turns.find((t) => t.turnId === turnId);
    const mergedCli =
      old?.lastCliState === 'running' && lastCliState == null ? 'running' : lastCliState;
    turns.push({
      turnId,
      sessionId,
      turnOrd,
      bridgeServerState,
      lastCliState: mergedCli,
      payload,
    });
  }
  turns.sort((a, b) => a.turnOrd - b.turnOrd);
  return { queueKey, updatedAt: new Date().toISOString(), turns };
}
